<template>
  <v-container class="container col-lg-10 col-sm-12">
    <h1 class="pb-3">Role</h1>
    <v-text-field
      placeholder="Type to find booking template"
      v-model="searchText"
      class="text-field__no-label mb-2"
    />
    <div>
      <v-skeleton-loader
        v-if="isLoadingTemplates"
        type="list-item-avatar-three-line"
      />
      <div
        v-else
        v-for="template in filteredTemplates"
        :key="template.id"
        class="job-template-container"
      >
        <JobItem
          class="job-template"
          :loading="selectedTemplate === template"
          @click.native="stepForward(template)"
          :role="template.jobTitle"
          :description="template.description"
        />
      </div>
    </div>
  </v-container>
</template>

<script>
import JobItem from "@/views/bookings/NewBooking/steps/components/JobItem";
import { DATA_NAMESPACE } from "@/store/modules/data";
import { createNamespacedHelpers } from "vuex";
import { FETCH_BOOKING_TEMPLATES } from "@/store/modules/data/actions";
import {
  GET_BOOKING_TEMPLATES,
  GET_IS_FETCHING_DATA
} from "@/store/modules/data/getters";

const { mapActions, mapGetters } = createNamespacedHelpers(DATA_NAMESPACE);

export default {
  name: "RoleStep",
  components: { JobItem },
  data() {
    return {
      searchText: "",
      isLoading: false,
      selectedTemplate: null,
      searchKeys: ["jobTitle", "description"]
    };
  },
  computed: {
    ...mapGetters({
      bookingTemplates: GET_BOOKING_TEMPLATES,
      isFetchingData: GET_IS_FETCHING_DATA
    }),
    filteredTemplates() {
      const qRaw = (this.searchText || '').trim();
  if (!qRaw) return this.bookingTemplates;

  const q = qRaw.toLowerCase();

  const valueFor = (item) =>
    this.searchKeys
      .map((k) => String(item[k] ?? '').toLowerCase())
      .join(' | ');

      // 1) strict substring filter
      const basicIncludes = (item) => valueFor(item).includes(q);

      // numeric-ish queries: stick to strict substring
      const numericish = /^[\d()\-\s]+$/.test(q);
      if (numericish) {
        return this.bookingTemplates.filter(basicIncludes);
      }

      // text queries: try strict substring first
      const substringHits = this.bookingTemplates.filter(basicIncludes);
      if (substringHits.length) {
        // optionally rank: earlier match first, then shorter text
        return substringHits.sort((a, b) => {
          const va = valueFor(a), vb = valueFor(b);
          const ia = va.indexOf(q), ib = vb.indexOf(q);
          return ia !== ib ? ia - ib : va.length - vb.length;
        });
      }

      // 2) fallback to fuzzy (tighten it!)
      return this.$search(q, this.bookingTemplates, {
        keys: this.searchKeys,
        threshold: 0.2,        // stricter (default ~0.6 is too loose)
        distance: 50,
        ignoreLocation: true,
        minMatchCharLength: Math.min(3, q.length),
        // useExtendedSearch: true, // if your $search passes this to Fuse, enable it
      });
    },
    isLoadingTemplates() {
      return this.isFetchingData && !this.bookingTemplates.length;
    }
  },
  created() {
    this.fetchBookingTemplates();
  },
  methods: {
    ...mapActions({
      fetchBookingTemplates: FETCH_BOOKING_TEMPLATES
    }),
    stepForward(template) {
      this.selectedTemplate = template;
      this.$emit("nextStep", { template });
    }
  }
};
</script>

<style lang="scss" scoped>
.job-template {
  padding: 10px;
  border-radius: $default-border-radius;
  cursor: pointer;
  &:hover {
    background-color: $background-hover;
  }
  &:active {
    background-color: $background-selected;
  }
}

.job-template-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
